import { isFgInt, fgInt } from './custom-types/fg-integer.js';
import { fgMatrix2D, fgMatrix3D, isFgMatrix2D, isFgMatrix3D } from './custom-types/fg-matrix.js';
import { crossVec3 } from '../math/cross-vec3.js';
import { dotVec3 } from '../math/dot-vec3.js';
import { mat4Compose } from '../math/mat4-compose.js';
import { mat4Decompose } from '../math/mat4-decompose.js';

function isVec2(v) {
  return typeof v === "object" && v !== null && "x" in v && "y" in v && !("z" in v);
}
function isMat4(v) {
  return (v instanceof Float32Array || v instanceof Float64Array) && v.length === 16;
}
function isVec3(v) {
  return typeof v === "object" && v !== null && "x" in v && "y" in v && "z" in v && !("w" in v);
}
function isVec4(v) {
  return typeof v === "object" && v !== null && "x" in v && "y" in v && "z" in v && "w" in v;
}
function binary(a, b, f) {
  if (isFgInt(a) && isFgInt(b)) {
    return fgInt(f(a.value, b.value));
  }
  if (typeof a === "number" && typeof b === "number") {
    return f(a, b);
  }
  if (isVec4(a) && isVec4(b)) {
    return { x: f(a.x, b.x), y: f(a.y, b.y), z: f(a.z, b.z), w: f(a.w, b.w) };
  }
  if (isVec3(a) && isVec3(b)) {
    return { x: f(a.x, b.x), y: f(a.y, b.y), z: f(a.z, b.z) };
  }
  if (isVec2(a) && isVec2(b)) {
    return { x: f(a.x, b.x), y: f(a.y, b.y) };
  }
  return a;
}
function unary(a, f) {
  if (isFgInt(a)) {
    return fgInt(f(a.value));
  }
  if (typeof a === "number") {
    return f(a);
  }
  if (isVec4(a)) {
    return { x: f(a.x), y: f(a.y), z: f(a.z), w: f(a.w) };
  }
  if (isVec3(a)) {
    return { x: f(a.x), y: f(a.y), z: f(a.z) };
  }
  if (isVec2(a)) {
    return { x: f(a.x), y: f(a.y) };
  }
  return a;
}
function fgAdd(a, b) {
  return binary(a, b, (x, y) => x + y);
}
function fgSub(a, b) {
  return binary(a, b, (x, y) => x - y);
}
function fgMul(a, b) {
  return binary(a, b, (x, y) => x * y);
}
function fgDiv(a, b) {
  return binary(a, b, (x, y) => x / y);
}
function fgRem(a, b) {
  return binary(a, b, (x, y) => x % y);
}
function fgAbs(a) {
  return unary(a, Math.abs);
}
function fgFloor(a) {
  return unary(a, Math.floor);
}
function fgClamp(a, b, c) {
  const lo = binary(a, b, (x, y) => Math.max(x, y));
  return binary(lo, c, (x, y) => Math.min(x, y));
}
function fgLt(a, b) {
  const x = isFgInt(a) ? a.value : a;
  const y = isFgInt(b) ? b.value : b;
  if (typeof x === "number" && typeof y === "number") {
    return x < y;
  }
  return false;
}
function fgCombine2(a, b) {
  const x = isFgInt(a) ? a.value : a;
  const y = isFgInt(b) ? b.value : b;
  return { x: x ?? 0, y: y ?? 0 };
}
function fgExtract2(v) {
  if (isVec2(v) || isVec3(v) || isVec4(v)) {
    return [v.x, v.y];
  }
  return [0, 0];
}
function num(v) {
  if (isFgInt(v)) {
    return v.value;
  }
  return typeof v === "number" ? v : NaN;
}
function num0(v) {
  const n = num(v);
  return Number.isNaN(n) && !(typeof v === "number") ? 0 : n;
}
function isNumeric(v) {
  return typeof v === "number" || isFgInt(v);
}
function toI(v) {
  return (isFgInt(v) ? v.value : v) | 0;
}
function ternary(a, b, c, f) {
  if (isFgInt(a) && isFgInt(b) && isFgInt(c)) {
    return fgInt(f(a.value, b.value, c.value));
  }
  if (typeof a === "number" && typeof b === "number" && typeof c === "number") {
    return f(a, b, c);
  }
  if (isVec4(a) && isVec4(b) && isVec4(c)) {
    return { x: f(a.x, b.x, c.x), y: f(a.y, b.y, c.y), z: f(a.z, b.z, c.z), w: f(a.w, b.w, c.w) };
  }
  if (isVec3(a) && isVec3(b) && isVec3(c)) {
    return { x: f(a.x, b.x, c.x), y: f(a.y, b.y, c.y), z: f(a.z, b.z, c.z) };
  }
  if (isVec2(a) && isVec2(b) && isVec2(c)) {
    return { x: f(a.x, b.x, c.x), y: f(a.y, b.y, c.y) };
  }
  return a;
}
function fgMin(a, b) {
  return binary(a, b, Math.min);
}
function fgMax(a, b) {
  return binary(a, b, Math.max);
}
function fgPow(a, b) {
  return binary(a, b, Math.pow);
}
function fgAtan2(a, b) {
  return binary(a, b, Math.atan2);
}
function fgNeg(a) {
  return unary(a, (x) => -x);
}
function fgSign(a) {
  return unary(a, Math.sign);
}
function fgCeil(a) {
  return unary(a, Math.ceil);
}
function fgRound(a) {
  return unary(a, Math.round);
}
function fgTrunc(a) {
  return unary(a, Math.trunc);
}
function fgFract(a) {
  return unary(a, (x) => x - Math.floor(x));
}
function fgSaturate(a) {
  return unary(a, (x) => Math.min(Math.max(x, 0), 1));
}
function fgSqrt(a) {
  return unary(a, Math.sqrt);
}
function fgCbrt(a) {
  return unary(a, Math.cbrt);
}
function fgExp(a) {
  return unary(a, Math.exp);
}
function fgLog(a) {
  return unary(a, Math.log);
}
function fgLog2(a) {
  return unary(a, Math.log2);
}
function fgLog10(a) {
  return unary(a, Math.log10);
}
function fgDegToRad(a) {
  return unary(a, (x) => x * Math.PI / 180);
}
function fgRadToDeg(a) {
  return unary(a, (x) => x * 180 / Math.PI);
}
function fgSin(a) {
  return unary(a, Math.sin);
}
function fgCos(a) {
  return unary(a, Math.cos);
}
function fgTan(a) {
  return unary(a, Math.tan);
}
function fgAsin(a) {
  return unary(a, Math.asin);
}
function fgAcos(a) {
  return unary(a, Math.acos);
}
function fgAtan(a) {
  return unary(a, Math.atan);
}
function fgSinh(a) {
  return unary(a, Math.sinh);
}
function fgCosh(a) {
  return unary(a, Math.cosh);
}
function fgTanh(a) {
  return unary(a, Math.tanh);
}
function fgAsinh(a) {
  return unary(a, Math.asinh);
}
function fgAcosh(a) {
  return unary(a, Math.acosh);
}
function fgAtanh(a) {
  return unary(a, Math.atanh);
}
function fgMix(a, b, t) {
  const tv = num(t);
  if (Number.isNaN(tv)) {
    return a;
  }
  const lerp = (x, y) => (1 - tv) * x + tv * y;
  if (isVec4(a) && isVec4(b)) {
    return { x: lerp(a.x, b.x), y: lerp(a.y, b.y), z: lerp(a.z, b.z), w: lerp(a.w, b.w) };
  }
  if (isVec3(a) && isVec3(b)) {
    return { x: lerp(a.x, b.x), y: lerp(a.y, b.y), z: lerp(a.z, b.z) };
  }
  if (isVec2(a) && isVec2(b)) {
    return { x: lerp(a.x, b.x), y: lerp(a.y, b.y) };
  }
  if (isNumeric(a) && isNumeric(b)) {
    const r = lerp(num(a), num(b));
    return isFgInt(a) && isFgInt(b) ? fgInt(r) : r;
  }
  return ternary(a, b, t, (x, y, z) => (1 - z) * x + z * y);
}
function fgSmoothStep(a, b, c) {
  return ternary(a, b, c, (edge0, edge1, value) => {
    const t = Math.min(Math.max((value - Math.min(edge0, edge1)) / Math.abs(edge1 - edge0), 0), 1);
    return t * t * (3 - 2 * t);
  });
}
function fgQuatSlerp(a, b, amount) {
  if (!isVec4(a) || !isVec4(b) || typeof amount !== "number") {
    return { x: 0, y: 0, z: 0, w: 1 };
  }
  let bx = b.x;
  let by = b.y;
  let bz = b.z;
  let bw = b.w;
  let dot = a.x * bx + a.y * by + a.z * bz + a.w * bw;
  if (dot < 0) {
    dot = -dot;
    bx = -bx;
    by = -by;
    bz = -bz;
    bw = -bw;
  }
  let left = 1 - amount;
  let right = amount;
  if (dot < 0.999999) {
    const omega = Math.acos(Math.min(1, Math.max(-1, dot)));
    const invSin = 1 / Math.sin(omega);
    left = Math.sin((1 - amount) * omega) * invSin;
    right = Math.sin(amount * omega) * invSin;
  }
  return {
    x: left * a.x + right * bx,
    y: left * a.y + right * by,
    z: left * a.z + right * bz,
    w: left * a.w + right * bw
  };
}
function fgEq(a, b) {
  if (isFgInt(a) && isFgInt(b)) {
    return a.value === b.value;
  }
  if (isFgInt(a) !== isFgInt(b)) {
    return false;
  }
  if (isVec4(a) && isVec4(b)) {
    return a.x === b.x && a.y === b.y && a.z === b.z && a.w === b.w;
  }
  if (isVec3(a) && isVec3(b)) {
    return a.x === b.x && a.y === b.y && a.z === b.z;
  }
  if (isVec2(a) && isVec2(b)) {
    return a.x === b.x && a.y === b.y;
  }
  return a === b;
}
function fgLe(a, b) {
  return isNumeric(a) && isNumeric(b) ? num(a) <= num(b) : false;
}
function fgGt(a, b) {
  return isNumeric(a) && isNumeric(b) ? num(a) > num(b) : false;
}
function fgGe(a, b) {
  return isNumeric(a) && isNumeric(b) ? num(a) >= num(b) : false;
}
function fgIsNaN(a) {
  return isNumeric(a) ? Number.isNaN(num(a)) : false;
}
function fgIsInf(a) {
  return isNumeric(a) ? !Number.isFinite(num(a)) : false;
}
function fgRandom(min = 0, max = 1) {
  return Math.random() * (max - min) + min;
}
function fgAnd(a, b) {
  if (typeof a === "boolean" && typeof b === "boolean") {
    return a && b;
  }
  if (typeof a === "number" && typeof b === "number") {
    return a & b;
  }
  if (isFgInt(a) && isFgInt(b)) {
    return fgInt(a.value & b.value);
  }
  return a;
}
function fgOr(a, b) {
  if (typeof a === "boolean" && typeof b === "boolean") {
    return a || b;
  }
  if (typeof a === "number" && typeof b === "number") {
    return a | b;
  }
  if (isFgInt(a) && isFgInt(b)) {
    return fgInt(a.value | b.value);
  }
  return a;
}
function fgXor(a, b) {
  if (typeof a === "boolean" && typeof b === "boolean") {
    return a !== b;
  }
  if (typeof a === "number" && typeof b === "number") {
    return a ^ b;
  }
  if (isFgInt(a) && isFgInt(b)) {
    return fgInt(a.value ^ b.value);
  }
  return a;
}
function fgNot(a) {
  if (typeof a === "boolean") {
    return !a;
  }
  if (typeof a === "number") {
    return ~a;
  }
  if (isFgInt(a)) {
    return fgInt(~a.value);
  }
  return a;
}
function fgLsl(a, b) {
  return fgInt(toI(a) << toI(b));
}
function fgAsr(a, b) {
  return fgInt(toI(a) >> toI(b));
}
function fgClz(a) {
  return fgInt(Math.clz32(toI(a)));
}
function fgCtz(a) {
  const n = toI(a);
  return fgInt(n ? 31 - Math.clz32(n & -n) : 32);
}
function fgPopcnt(a) {
  let n = toI(a) >>> 0;
  let r = 0;
  while (n) {
    r += n & 1;
    n >>>= 1;
  }
  return fgInt(r);
}
function fgLength(a) {
  if (isVec2(a)) {
    return Math.hypot(a.x, a.y);
  }
  if (isVec4(a)) {
    return Math.hypot(a.x, a.y, a.z, a.w);
  }
  if (isVec3(a)) {
    return Math.hypot(a.x, a.y, a.z);
  }
  return 0;
}
function fgNormalize(a) {
  const len = fgLength(a);
  const s = len === 0 ? 0 : 1 / len;
  if (isVec2(a)) {
    return { x: a.x * s, y: a.y * s };
  }
  if (isVec4(a)) {
    return { x: a.x * s, y: a.y * s, z: a.z * s, w: a.w * s };
  }
  if (isVec3(a)) {
    return { x: a.x * s, y: a.y * s, z: a.z * s };
  }
  return a;
}
function fgDot(a, b) {
  if (isVec2(a) && isVec2(b)) {
    return a.x * b.x + a.y * b.y;
  }
  if (isVec4(a) && isVec4(b)) {
    return a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w;
  }
  if (isVec3(a) && isVec3(b)) {
    return dotVec3(a, b);
  }
  return 0;
}
function fgCross(a, b) {
  if (isVec3(a) && isVec3(b)) {
    return crossVec3(a, b);
  }
  return a;
}
const SLERP_EPSILON = 1e-6;
function perpendicular(v) {
  const axis = Math.abs(v.x) <= Math.abs(v.y) && Math.abs(v.x) <= Math.abs(v.z) ? { x: 1, y: 0, z: 0 } : Math.abs(v.y) <= Math.abs(v.z) ? { x: 0, y: 1, z: 0 } : { x: 0, y: 0, z: 1 };
  const cross = crossVec3(v, axis);
  const invLength = 1 / Math.hypot(cross.x, cross.y, cross.z);
  return { x: cross.x * invLength, y: cross.y * invLength, z: cross.z * invLength };
}
function fgVectorSlerp(a, b, amount) {
  if (typeof amount !== "number") {
    return a;
  }
  if (isVec2(a) && isVec2(b)) {
    const lengthA = Math.hypot(a.x, a.y);
    const lengthB = Math.hypot(b.x, b.y);
    if (lengthA < SLERP_EPSILON || lengthB < SLERP_EPSILON) {
      return { x: (1 - amount) * a.x + amount * b.x, y: (1 - amount) * a.y + amount * b.y };
    }
    const ax = a.x / lengthA;
    const ay = a.y / lengthA;
    const bx = b.x / lengthB;
    const by = b.y / lengthB;
    let angle = Math.acos(Math.min(1, Math.max(-1, ax * bx + ay * by)));
    if (ax * by - ay * bx < 0) {
      angle = -angle;
    }
    const length = (1 - amount) * lengthA + amount * lengthB;
    const cos = Math.cos(amount * angle);
    const sin = Math.sin(amount * angle);
    return { x: (ax * cos - ay * sin) * length, y: (ax * sin + ay * cos) * length };
  }
  if (isVec3(a) && isVec3(b)) {
    const lengthA = Math.hypot(a.x, a.y, a.z);
    const lengthB = Math.hypot(b.x, b.y, b.z);
    const lerp = () => ({
      x: (1 - amount) * a.x + amount * b.x,
      y: (1 - amount) * a.y + amount * b.y,
      z: (1 - amount) * a.z + amount * b.z
    });
    if (lengthA < SLERP_EPSILON || lengthB < SLERP_EPSILON) {
      return lerp();
    }
    const unitA = { x: a.x / lengthA, y: a.y / lengthA, z: a.z / lengthA };
    const unitB = { x: b.x / lengthB, y: b.y / lengthB, z: b.z / lengthB };
    const dot = dotVec3(unitA, unitB);
    if (dot > 1 - SLERP_EPSILON) {
      return lerp();
    }
    const axis = dot < -1 + SLERP_EPSILON ? perpendicular(unitA) : (() => {
      const cross = crossVec3(unitA, unitB);
      const invLength = 1 / Math.hypot(cross.x, cross.y, cross.z);
      return { x: cross.x * invLength, y: cross.y * invLength, z: cross.z * invLength };
    })();
    const angle = amount * Math.acos(Math.min(1, Math.max(-1, dot)));
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const axisCrossA = crossVec3(axis, unitA);
    const length = (1 - amount) * lengthA + amount * lengthB;
    return {
      x: (unitA.x * cos + axisCrossA.x * sin) * length,
      y: (unitA.y * cos + axisCrossA.y * sin) * length,
      z: (unitA.z * cos + axisCrossA.z * sin) * length
    };
  }
  return a;
}
function fgRotate2D(a, angle) {
  if (isVec2(a) && typeof angle === "number") {
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    return { x: c * a.x - s * a.y, y: s * a.x + c * a.y };
  }
  return a;
}
function fgRotate3D(a, q) {
  if (isVec3(a) && isVec4(q)) {
    const { x, y, z } = a;
    const tx = 2 * (q.y * z - q.z * y);
    const ty = 2 * (q.z * x - q.x * z);
    const tz = 2 * (q.x * y - q.y * x);
    return {
      x: x + q.w * tx + (q.y * tz - q.z * ty),
      y: y + q.w * ty + (q.z * tx - q.x * tz),
      z: z + q.w * tz + (q.x * ty - q.y * tx)
    };
  }
  return a;
}
function fgCombine3(a, b, c) {
  return { x: num0(a), y: num0(b), z: num0(c) };
}
function fgCombine4(a, b, c, d) {
  return { x: num0(a), y: num0(b), z: num0(c), w: num0(d) };
}
function fgExtract3(v) {
  if (isVec3(v) || isVec4(v)) {
    return [v.x, v.y, v.z];
  }
  return [0, 0, 0];
}
function fgExtract4(v) {
  if (isVec4(v)) {
    return [v.x, v.y, v.z, v.w];
  }
  return [0, 0, 0, 0];
}
function fgConjugate(a) {
  if (isVec4(a)) {
    return { x: -a.x, y: -a.y, z: -a.z, w: a.w };
  }
  return a;
}
function fgQuatMul(a, b) {
  if (isVec4(a) && isVec4(b)) {
    return quatMul(a, b);
  }
  return a;
}
function quatMul(a, b) {
  return {
    x: a.x * b.w + a.w * b.x + a.y * b.z - a.z * b.y,
    y: a.y * b.w + a.w * b.y + a.z * b.x - a.x * b.z,
    z: a.z * b.w + a.w * b.z + a.x * b.y - a.y * b.x,
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z
  };
}
function fgTransformVector(v, m) {
  if (isVec2(v) && isFgMatrix2D(m)) {
    const mm = m.m;
    return { x: mm[0] * v.x + mm[2] * v.y, y: mm[1] * v.x + mm[3] * v.y };
  }
  if (isVec3(v) && isFgMatrix3D(m)) {
    const mm = m.m;
    return {
      x: mm[0] * v.x + mm[3] * v.y + mm[6] * v.z,
      y: mm[1] * v.x + mm[4] * v.y + mm[7] * v.z,
      z: mm[2] * v.x + mm[5] * v.y + mm[8] * v.z
    };
  }
  if (isVec4(v) && isMat4(m)) {
    return {
      x: m[0] * v.x + m[4] * v.y + m[8] * v.z + m[12] * v.w,
      y: m[1] * v.x + m[5] * v.y + m[9] * v.z + m[13] * v.w,
      z: m[2] * v.x + m[6] * v.y + m[10] * v.z + m[14] * v.w,
      w: m[3] * v.x + m[7] * v.y + m[11] * v.z + m[15] * v.w
    };
  }
  return v;
}
function fgCombineMatrix2D(inputs) {
  return fgMatrix2D(inputs);
}
function fgCombineMatrix3D(inputs) {
  return fgMatrix3D(inputs);
}
function fgCombineMatrix(inputs) {
  const out = new Float32Array(16);
  for (let i = 0; i < 16; i++) {
    out[i] = inputs[i] ?? 0;
  }
  return out;
}
function fgExtractMatrix2D(m) {
  if (isFgMatrix2D(m)) {
    return [m.m[0], m.m[1], m.m[2], m.m[3]];
  }
  return [0, 0, 0, 0];
}
function fgExtractMatrix3D(m) {
  if (isFgMatrix3D(m)) {
    return [m.m[0], m.m[1], m.m[2], m.m[3], m.m[4], m.m[5], m.m[6], m.m[7], m.m[8]];
  }
  return [0, 0, 0, 0, 0, 0, 0, 0, 0];
}
function fgExtractMatrix(m) {
  if (isMat4(m)) {
    return Array.from({ length: 16 }, (_, i) => m[i]);
  }
  return Array.from({ length: 16 }, () => 0);
}
function fgTranspose(m) {
  if (isFgMatrix2D(m)) {
    const mm = m.m;
    return fgMatrix2D([mm[0], mm[2], mm[1], mm[3]]);
  }
  if (isFgMatrix3D(m)) {
    const mm = m.m;
    return fgMatrix3D([mm[0], mm[3], mm[6], mm[1], mm[4], mm[7], mm[2], mm[5], mm[8]]);
  }
  if (isMat4(m)) {
    const out = new Float32Array(16);
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        out[j * 4 + i] = m[i * 4 + j];
      }
    }
    return out;
  }
  return m;
}
function fgDeterminant(m) {
  if (isFgMatrix2D(m)) {
    const mm = m.m;
    return mm[0] * mm[3] - mm[1] * mm[2];
  }
  if (isFgMatrix3D(m)) {
    const mm = m.m;
    return mm[0] * (mm[4] * mm[8] - mm[7] * mm[5]) - mm[3] * (mm[1] * mm[8] - mm[7] * mm[2]) + mm[6] * (mm[1] * mm[5] - mm[4] * mm[2]);
  }
  if (isMat4(m)) {
    const a00 = m[0], a01 = m[1], a02 = m[2], a03 = m[3];
    const a10 = m[4], a11 = m[5], a12 = m[6], a13 = m[7];
    const a20 = m[8], a21 = m[9], a22 = m[10], a23 = m[11];
    const a30 = m[12], a31 = m[13], a32 = m[14], a33 = m[15];
    const b00 = a00 * a11 - a01 * a10, b01 = a00 * a12 - a02 * a10;
    const b02 = a00 * a13 - a03 * a10, b03 = a01 * a12 - a02 * a11;
    const b04 = a01 * a13 - a03 * a11, b05 = a02 * a13 - a03 * a12;
    const b06 = a20 * a31 - a21 * a30, b07 = a20 * a32 - a22 * a30;
    const b08 = a20 * a33 - a23 * a30, b09 = a21 * a32 - a22 * a31;
    const b10 = a21 * a33 - a23 * a31, b11 = a22 * a33 - a23 * a32;
    return b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
  }
  return 0;
}
function fgMat4Invert(input) {
  const m = input;
  const a00 = m[0], a01 = m[1], a02 = m[2], a03 = m[3];
  const a10 = m[4], a11 = m[5], a12 = m[6], a13 = m[7];
  const a20 = m[8], a21 = m[9], a22 = m[10], a23 = m[11];
  const a30 = m[12], a31 = m[13], a32 = m[14], a33 = m[15];
  const b00 = a00 * a11 - a01 * a10;
  const b01 = a00 * a12 - a02 * a10;
  const b02 = a00 * a13 - a03 * a10;
  const b03 = a01 * a12 - a02 * a11;
  const b04 = a01 * a13 - a03 * a11;
  const b05 = a02 * a13 - a03 * a12;
  const b06 = a20 * a31 - a21 * a30;
  const b07 = a20 * a32 - a22 * a30;
  const b08 = a20 * a33 - a23 * a30;
  const b09 = a21 * a32 - a22 * a31;
  const b10 = a21 * a33 - a23 * a31;
  const b11 = a22 * a33 - a23 * a32;
  let det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
  if (Math.abs(det) < 1e-10) {
    return null;
  }
  det = 1 / det;
  const out = new Float32Array(16);
  out[0] = (a11 * b11 - a12 * b10 + a13 * b09) * det;
  out[1] = (a02 * b10 - a01 * b11 - a03 * b09) * det;
  out[2] = (a31 * b05 - a32 * b04 + a33 * b03) * det;
  out[3] = (a22 * b04 - a21 * b05 - a23 * b03) * det;
  out[4] = (a12 * b08 - a10 * b11 - a13 * b07) * det;
  out[5] = (a00 * b11 - a02 * b08 + a03 * b07) * det;
  out[6] = (a32 * b02 - a30 * b05 - a33 * b01) * det;
  out[7] = (a20 * b05 - a22 * b02 + a23 * b01) * det;
  out[8] = (a10 * b10 - a11 * b08 + a13 * b06) * det;
  out[9] = (a01 * b08 - a00 * b10 - a03 * b06) * det;
  out[10] = (a30 * b04 - a31 * b02 + a33 * b00) * det;
  out[11] = (a21 * b02 - a20 * b04 - a23 * b00) * det;
  out[12] = (a11 * b07 - a10 * b09 - a12 * b06) * det;
  out[13] = (a00 * b09 - a01 * b07 + a02 * b06) * det;
  out[14] = (a31 * b01 - a30 * b03 - a32 * b00) * det;
  out[15] = (a20 * b03 - a21 * b01 + a22 * b00) * det;
  return out;
}
function fgMat4Multiply(a, b) {
  const x = a;
  const y = b;
  const a0 = x[0], a1 = x[1], a2 = x[2], a3 = x[3];
  const a4 = x[4], a5 = x[5], a6 = x[6], a7 = x[7];
  const a8 = x[8], a9 = x[9], a10 = x[10], a11 = x[11];
  const a12 = x[12], a13 = x[13], a14 = x[14], a15 = x[15];
  const out = new Float32Array(16);
  let b0 = y[0], b1 = y[1], b2 = y[2], b3 = y[3];
  out[0] = a0 * b0 + a4 * b1 + a8 * b2 + a12 * b3;
  out[1] = a1 * b0 + a5 * b1 + a9 * b2 + a13 * b3;
  out[2] = a2 * b0 + a6 * b1 + a10 * b2 + a14 * b3;
  out[3] = a3 * b0 + a7 * b1 + a11 * b2 + a15 * b3;
  b0 = y[4];
  b1 = y[5];
  b2 = y[6];
  b3 = y[7];
  out[4] = a0 * b0 + a4 * b1 + a8 * b2 + a12 * b3;
  out[5] = a1 * b0 + a5 * b1 + a9 * b2 + a13 * b3;
  out[6] = a2 * b0 + a6 * b1 + a10 * b2 + a14 * b3;
  out[7] = a3 * b0 + a7 * b1 + a11 * b2 + a15 * b3;
  b0 = y[8];
  b1 = y[9];
  b2 = y[10];
  b3 = y[11];
  out[8] = a0 * b0 + a4 * b1 + a8 * b2 + a12 * b3;
  out[9] = a1 * b0 + a5 * b1 + a9 * b2 + a13 * b3;
  out[10] = a2 * b0 + a6 * b1 + a10 * b2 + a14 * b3;
  out[11] = a3 * b0 + a7 * b1 + a11 * b2 + a15 * b3;
  b0 = y[12];
  b1 = y[13];
  b2 = y[14];
  b3 = y[15];
  out[12] = a0 * b0 + a4 * b1 + a8 * b2 + a12 * b3;
  out[13] = a1 * b0 + a5 * b1 + a9 * b2 + a13 * b3;
  out[14] = a2 * b0 + a6 * b1 + a10 * b2 + a14 * b3;
  out[15] = a3 * b0 + a7 * b1 + a11 * b2 + a15 * b3;
  return out;
}
function fgMat4Identity() {
  const out = new Float32Array(16);
  out[0] = 1;
  out[5] = 1;
  out[10] = 1;
  out[15] = 1;
  return out;
}
function fgInvertMatrix(m) {
  if (isFgMatrix2D(m)) {
    const mm = m.m;
    const det = mm[0] * mm[3] - mm[1] * mm[2];
    if (Math.abs(det) < 1e-10) {
      return fgMatrix2D();
    }
    const d = 1 / det;
    return fgMatrix2D([mm[3] * d, -mm[1] * d, -mm[2] * d, mm[0] * d]);
  }
  if (isFgMatrix3D(m)) {
    const mm = m.m;
    const det = mm[0] * (mm[4] * mm[8] - mm[7] * mm[5]) - mm[3] * (mm[1] * mm[8] - mm[7] * mm[2]) + mm[6] * (mm[1] * mm[5] - mm[4] * mm[2]);
    if (Math.abs(det) < 1e-10) {
      return fgMatrix3D();
    }
    const d = 1 / det;
    return fgMatrix3D([
      (mm[4] * mm[8] - mm[7] * mm[5]) * d,
      -(mm[1] * mm[8] - mm[7] * mm[2]) * d,
      (mm[1] * mm[5] - mm[4] * mm[2]) * d,
      -(mm[3] * mm[8] - mm[6] * mm[5]) * d,
      (mm[0] * mm[8] - mm[6] * mm[2]) * d,
      -(mm[0] * mm[5] - mm[3] * mm[2]) * d,
      (mm[3] * mm[7] - mm[6] * mm[4]) * d,
      -(mm[0] * mm[7] - mm[6] * mm[1]) * d,
      (mm[0] * mm[4] - mm[3] * mm[1]) * d
    ]);
  }
  if (isMat4(m)) {
    return fgMat4Invert(m) ?? fgMat4Identity();
  }
  return m;
}
function fgMatrixMultiplication(a, b) {
  if (isFgMatrix2D(a) && isFgMatrix2D(b)) {
    const am = a.m, bm = b.m;
    return fgMatrix2D([am[0] * bm[0] + am[2] * bm[1], am[1] * bm[0] + am[3] * bm[1], am[0] * bm[2] + am[2] * bm[3], am[1] * bm[2] + am[3] * bm[3]]);
  }
  if (isFgMatrix3D(a) && isFgMatrix3D(b)) {
    const am = a.m, bm = b.m;
    return fgMatrix3D([
      am[0] * bm[0] + am[3] * bm[1] + am[6] * bm[2],
      am[1] * bm[0] + am[4] * bm[1] + am[7] * bm[2],
      am[2] * bm[0] + am[5] * bm[1] + am[8] * bm[2],
      am[0] * bm[3] + am[3] * bm[4] + am[6] * bm[5],
      am[1] * bm[3] + am[4] * bm[4] + am[7] * bm[5],
      am[2] * bm[3] + am[5] * bm[4] + am[8] * bm[5],
      am[0] * bm[6] + am[3] * bm[7] + am[6] * bm[8],
      am[1] * bm[6] + am[4] * bm[7] + am[7] * bm[8],
      am[2] * bm[6] + am[5] * bm[7] + am[8] * bm[8]
    ]);
  }
  if (isMat4(a) && isMat4(b)) {
    return fgMat4Multiply(a, b);
  }
  return a;
}
function fgMatrixCompose(pos, quat, scale) {
  const p = isVec3(pos) ? pos : { x: 0, y: 0, z: 0 };
  const q = isVec4(quat) ? quat : { x: 0, y: 0, z: 0, w: 1 };
  const s = isVec3(scale) ? scale : { x: 1, y: 1, z: 1 };
  return mat4Compose(p.x, p.y, p.z, q.x, q.y, q.z, q.w, s.x, s.y, s.z);
}
function fgMatrixDecompose(m) {
  const zero = { x: 0, y: 0, z: 0 };
  const identQ = { x: 0, y: 0, z: 0, w: 1 };
  const oneS = { x: 1, y: 1, z: 1 };
  if (!isMat4(m)) {
    return { position: zero, rotationQuaternion: identQ, scaling: oneS, isValid: false };
  }
  const r0 = Math.round(m[3] * 1e4) / 1e4;
  const r1 = Math.round(m[7] * 1e4) / 1e4;
  const r2 = Math.round(m[11] * 1e4) / 1e4;
  const r3 = Math.round(m[15] * 1e4) / 1e4;
  if (r0 !== 0 || r1 !== 0 || r2 !== 0 || r3 !== 1) {
    return { position: zero, rotationQuaternion: identQ, scaling: oneS, isValid: false };
  }
  const { translation, rotation, scale } = mat4Decompose(m);
  return { position: translation, rotationQuaternion: rotation, scaling: scale, isValid: true };
}
function fgAngleBetween(a, b) {
  if (isVec4(a) && isVec4(b)) {
    const d = Math.min(1, Math.max(-1, a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w));
    return 2 * Math.acos(d);
  }
  return 0;
}
function fgQuaternionFromAxisAngle(axis, angle) {
  const n = num(angle);
  if (!isVec3(axis) || Number.isNaN(n)) {
    return { x: 0, y: 0, z: 0, w: 1 };
  }
  const half = n / 2;
  const s = Math.sin(half);
  return { x: axis.x * s, y: axis.y * s, z: axis.z * s, w: Math.cos(half) };
}
function fgAxisAngleFromQuaternion(q) {
  if (!isVec4(q)) {
    return { axis: { x: 1, y: 0, z: 0 }, angle: 0, isValid: false };
  }
  const w = Math.min(1, Math.max(-1, q.w));
  const angle = 2 * Math.acos(w);
  const s = Math.sqrt(Math.max(0, 1 - w * w));
  if (s < 1e-7) {
    return { axis: { x: 1, y: 0, z: 0 }, angle, isValid: true };
  }
  return { axis: { x: q.x / s, y: q.y / s, z: q.z / s }, angle, isValid: true };
}
function fgQuaternionFromDirections(a, b) {
  if (!isVec3(a) || !isVec3(b)) {
    return { x: 0, y: 0, z: 0, w: 1 };
  }
  const dot = dotVec3(a, b);
  if (Number.isFinite(dot) && dot > 1 - SLERP_EPSILON) {
    return { x: 0, y: 0, z: 0, w: 1 };
  }
  if (Number.isFinite(dot) && dot < -1 + SLERP_EPSILON) {
    const axis = perpendicular(a);
    return { x: axis.x, y: axis.y, z: axis.z, w: 0 };
  }
  const cross = crossVec3(a, b);
  const invLength = 1 / Math.hypot(cross.x, cross.y, cross.z);
  const axisScale = Math.sqrt(0.5 - 0.5 * dot);
  return {
    x: cross.x * invLength * axisScale,
    y: cross.y * invLength * axisScale,
    z: cross.z * invLength * axisScale,
    w: Math.sqrt(0.5 + 0.5 * dot)
  };
}
function quaternionFromBasis(m11, m12, m13, m21, m22, m23, m31, m32, m33) {
  const trace = m11 + m22 + m33;
  let s;
  if (trace > 0) {
    s = 0.5 / Math.sqrt(trace + 1);
    return { x: (m32 - m23) * s, y: (m13 - m31) * s, z: (m21 - m12) * s, w: 0.25 / s };
  }
  if (m11 > m22 && m11 > m33) {
    s = 2 * Math.sqrt(1 + m11 - m22 - m33);
    return { x: 0.25 * s, y: (m12 + m21) / s, z: (m13 + m31) / s, w: (m32 - m23) / s };
  }
  if (m22 > m33) {
    s = 2 * Math.sqrt(1 + m22 - m11 - m33);
    return { x: (m12 + m21) / s, y: 0.25 * s, z: (m23 + m32) / s, w: (m13 - m31) / s };
  }
  s = 2 * Math.sqrt(1 + m33 - m11 - m22);
  return { x: (m13 + m31) / s, y: (m23 + m32) / s, z: 0.25 * s, w: (m21 - m12) / s };
}
function fgQuaternionFromUpForward(up, forward) {
  if (!isVec3(up) || !isVec3(forward)) {
    return { x: 0, y: 0, z: 0, w: 1 };
  }
  let side = crossVec3(up, forward);
  const sideLength = Math.hypot(side.x, side.y, side.z);
  if (sideLength < SLERP_EPSILON) {
    side = perpendicular(forward);
  } else {
    side = { x: side.x / sideLength, y: side.y / sideLength, z: side.z / sideLength };
  }
  const correctedUp = crossVec3(forward, side);
  return quaternionFromBasis(side.x, correctedUp.x, forward.x, side.y, correctedUp.y, forward.y, side.z, correctedUp.z, forward.z);
}
const QUATERNION_ANGLE_ORDERS = ["xyz", "xzy", "yxz", "yzx", "zxy", "zyx"];
function fgQuaternionFromAngles(x, y, z, order) {
  const angleX = num(x);
  const angleY = num(y);
  const angleZ = num(z);
  if (Number.isNaN(angleX) || Number.isNaN(angleY) || Number.isNaN(angleZ)) {
    return { x: NaN, y: NaN, z: NaN, w: NaN };
  }
  const qx = fgQuaternionFromAxisAngle({ x: 1, y: 0, z: 0 }, angleX);
  const qy = fgQuaternionFromAxisAngle({ x: 0, y: 1, z: 0 }, angleY);
  const qz = fgQuaternionFromAxisAngle({ x: 0, y: 0, z: 1 }, angleZ);
  const rotationOrder = typeof order === "string" && QUATERNION_ANGLE_ORDERS.includes(order) ? order : "yxz";
  const quaternions = { x: qx, y: qy, z: qz };
  return quatMul(
    quatMul(quaternions[rotationOrder[0]], quaternions[rotationOrder[1]]),
    quaternions[rotationOrder[2]]
  );
}
function fgRgbToOkLCh(r, g, b) {
  const red = num(r);
  const green = num(g);
  const blue = num(b);
  const long = 0.4122214708 * red + 0.5363325363 * green + 0.0514459929 * blue;
  const medium = 0.2119034982 * red + 0.6806995451 * green + 0.1073969566 * blue;
  const short = 0.0883024619 * red + 0.2817188376 * green + 0.6299787005 * blue;
  const lRoot = Math.cbrt(long);
  const mRoot = Math.cbrt(medium);
  const sRoot = Math.cbrt(short);
  const okL = 0.2104542553 * lRoot + 0.793617785 * mRoot - 0.0040720468 * sRoot;
  const okA = 1.9779984951 * lRoot - 2.428592205 * mRoot + 0.4505937099 * sRoot;
  const okB = 0.0259040371 * lRoot + 0.7827717662 * mRoot - 0.808675766 * sRoot;
  return { l: okL, c: Math.hypot(okA, okB), h: Math.atan2(okB, okA) };
}
function fgRgbFromOkLCh(l, c, h) {
  const lightness = num(l);
  const chroma = num(c);
  const hue = num(h);
  const okA = chroma * Math.cos(hue);
  const okB = chroma * Math.sin(hue);
  const lPrime = lightness + 0.3963377774 * okA + 0.2158037573 * okB;
  const mPrime = lightness - 0.1055613458 * okA - 0.0638541728 * okB;
  const sPrime = lightness - 0.0894841775 * okA - 1.291485548 * okB;
  const long = lPrime * lPrime * lPrime;
  const medium = mPrime * mPrime * mPrime;
  const short = sPrime * sPrime * sPrime;
  return {
    r: 4.0767416621 * long - 3.3077115913 * medium + 0.2309699292 * short,
    g: -1.2684380046 * long + 2.6097574011 * medium - 0.3413193965 * short,
    b: -0.0041960863 * long - 0.7034186147 * medium + 1.707614701 * short
  };
}

export { fgAbs, fgAcos, fgAcosh, fgAdd, fgAnd, fgAngleBetween, fgAsin, fgAsinh, fgAsr, fgAtan, fgAtan2, fgAtanh, fgAxisAngleFromQuaternion, fgCbrt, fgCeil, fgClamp, fgClz, fgCombine2, fgCombine3, fgCombine4, fgCombineMatrix, fgCombineMatrix2D, fgCombineMatrix3D, fgConjugate, fgCos, fgCosh, fgCross, fgCtz, fgDegToRad, fgDeterminant, fgDiv, fgDot, fgEq, fgExp, fgExtract2, fgExtract3, fgExtract4, fgExtractMatrix, fgExtractMatrix2D, fgExtractMatrix3D, fgFloor, fgFract, fgGe, fgGt, fgInvertMatrix, fgIsInf, fgIsNaN, fgLe, fgLength, fgLog, fgLog10, fgLog2, fgLsl, fgLt, fgMatrixCompose, fgMatrixDecompose, fgMatrixMultiplication, fgMax, fgMin, fgMix, fgMul, fgNeg, fgNormalize, fgNot, fgOr, fgPopcnt, fgPow, fgQuatMul, fgQuatSlerp, fgQuaternionFromAngles, fgQuaternionFromAxisAngle, fgQuaternionFromDirections, fgQuaternionFromUpForward, fgRadToDeg, fgRandom, fgRem, fgRgbFromOkLCh, fgRgbToOkLCh, fgRotate2D, fgRotate3D, fgRound, fgSaturate, fgSign, fgSin, fgSinh, fgSmoothStep, fgSqrt, fgSub, fgTan, fgTanh, fgTransformVector, fgTranspose, fgTrunc, fgVectorSlerp, fgXor };
//# sourceMappingURL=fg-math.js.map
